import { useCallback } from 'react';

import { Tasks } from '@air/api';
import { type CustomField } from '@air/api/types';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type AssetCustomFieldValueUpdateTask, type GetBaseMetadata } from '~/store/tasks/types';
import { useCreateTask } from '~/utils/taskUtils/useCreateTask';

export type CreatePrivateAssetCustomFieldValueUpdateTaskParams = Omit<
  GetBaseMetadata<AssetCustomFieldValueUpdateTask>,
  'baseCustomField'
> & {
  baseCustomField: CustomField;
};

export const useCreatePrivateAssetCustomFieldValueUpdateTask = () => {
  const { createTask } = useCreateTask();
  const { currentWorkspace } = useCurrentWorkspace();

  const createPrivateAssetCustomFieldValueUpdateTask = useCallback(
    async ({
      boardId,
      assetIds,
      includeDescendants,
      baseCustomField,
      operation,
      valueId,
      valueIdsToAdd,
      valueIdsToRemove,
      plainTextValue,
      dateValue,
      trackLocation,
    }: CreatePrivateAssetCustomFieldValueUpdateTaskParams) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      const taskValueId = operation === 'singleSelect' ? (valueId ?? null) : undefined;
      const taskDateValue = operation === 'date' ? (dateValue ?? null) : undefined;

      const taskArgs = {
        boardId,
        assetIds,
        includeDescendants,
        customFieldId: baseCustomField.id,
        valueId: taskValueId,
        valueIdsToAdd: valueIdsToAdd?.length ? valueIdsToAdd : undefined,
        valueIdsToRemove: valueIdsToRemove?.length ? valueIdsToRemove : undefined,
        plainTextValue,
        dateValue: taskDateValue,
      };

      return createTask({
        task: {
          metadata: {
            boardId,
            assetIds,
            includeDescendants,
            baseCustomField,
            operation,
            valueId: taskValueId,
            valueIdsToAdd,
            valueIdsToRemove,
            plainTextValue,
            dateValue: taskDateValue,
            trackLocation,
          },
          type: 'AssetCustomFieldValueUpdate',
          workspaceId,
        },
        createTask: () =>
          Tasks.createTask({
            workspaceId,
            task: {
              type: 'AssetCustomFieldValueUpdater',
              args: taskArgs,
            },
          }),
      });
    },
    [createTask, currentWorkspace?.id],
  );

  return {
    createPrivateAssetCustomFieldValueUpdateTask,
  };
};
